import { Helmet } from 'react-helmet-async';
import { Phone, AlertTriangle, DollarSign, Clock, Shield, CheckCircle2, Sun, Droplets, Wind } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AsphaltShingles = () => {
  // Schema for SEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Asphalt Shingle Roof Coating",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Savage Rejuvenation Coatings",
      "telephone": "+1-321-804-4413"
    },
    "areaServed": "Central Florida",
    "description": "Professional asphalt shingle roof coating services in Orlando, Tampa, and Central Florida. Extend roof life, reduce cooling costs, and protect against Florida weather."
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://savagerejuvenationcoatings.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Asphalt Shingle Roofs",
        "item": "https://savagerejuvenationcoatings.com/roofs/asphalt-shingles"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Asphalt Shingle Roof Problems & Coating Solutions | Orlando Tampa FL | Savage Rejuvenation</title>
        <meta name="description" content="Learn about asphalt shingle roof vulnerabilities in Florida weather. Get repair costs, damage statistics, and discover how protective coating can save you thousands. Call (321) 804-4413." />
        <meta name="keywords" content="asphalt shingle roof problems Florida, shingle roof repair cost Orlando, asphalt roof coating Tampa, shingle roof damage, Florida roof protection" />
        
        <link rel="canonical" href="https://savagerejuvenationcoatings.com/roofs/asphalt-shingles" />
        
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-secondary border-b-4 border-primary">
          <div className="max-w-7xl mx-auto px-4 md:px-12 py-3 flex items-center justify-between">
            <nav className="hidden lg:flex items-center gap-6">
              <a href="/" className="text-white hover:text-primary font-manrope font-semibold">Home</a>
              <a href="/reviews" className="text-white hover:text-primary font-manrope font-semibold">Reviews</a>
              <a href="/qa" className="text-white hover:text-primary font-manrope font-semibold">Q&A</a>
              <a href="/blog" className="text-white hover:text-primary font-manrope font-semibold">Blog</a>
            </nav>
            <div className="hidden lg:flex items-center gap-6">
              <a href="tel:3218044413" className="text-white hover:text-primary transition-colors flex items-center gap-2 text-lg font-manrope font-semibold">
                <Phone className="h-5 w-5" />
                (321) 804-4413
              </a>
            </div>
            <div className="lg:hidden">
              <a href="tel:3218044413">
                <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[2px_2px_0px_0px_#000] rounded-sm px-3 py-2">
                  <Phone className="h-5 w-5" />
                </Button>
              </a>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section 
          className="relative min-h-[500px] flex items-center bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.6)), url('https://images.unsplash.com/photo-1680035579849-3185807fb1ea?crop=entropy&cs=srgb&fm=jpg&q=85')`
          }}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-white">
            <h1 className="font-barlow text-4xl md:text-6xl font-bold uppercase mb-6">
              Asphalt Shingle Roofs
            </h1>
            <p className="text-xl md:text-2xl font-manrope max-w-3xl mb-8">
              The most common roof type in Florida—and the most vulnerable to our brutal climate. 
              Learn the real costs and how to protect your investment.
            </p>
            <a href="tel:3218044413">
              <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[4px_4px_0px_0px_#000] rounded-sm text-lg px-8 py-6 font-barlow uppercase font-bold">
                <Phone className="mr-2 h-5 w-5" />
                Get FREE Inspection
              </Button>
            </a>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-barlow text-3xl md:text-4xl font-bold uppercase mb-8 text-center">
              About Asphalt Shingle Roofs
            </h2>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="font-manrope text-lg text-gray-700 mb-6">
                  Asphalt shingles are the most popular roofing material in America, covering approximately 
                  <strong className="text-primary"> 80% of all residential homes</strong>. They're affordable, 
                  versatile, and come in many colors and styles.
                </p>
                <p className="font-manrope text-lg text-gray-700 mb-6">
                  However, in Florida's extreme climate, asphalt shingles face unique challenges. The combination 
                  of intense UV radiation, high humidity, heavy rainfall, and hurricane-force winds can 
                  significantly reduce their lifespan.
                </p>
                <div className="bg-accent-light border-2 border-black p-6">
                  <h3 className="font-barlow text-xl font-bold uppercase mb-3">Quick Facts</h3>
                  <ul className="space-y-2 font-manrope">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <span>Average lifespan: 15-30 years (less in Florida)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <span>Made of fiberglass mat coated with asphalt</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <span>Cost-effective initial installation</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <span>Florida lifespan often reduced to 12-20 years</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1680035579849-3185807fb1ea?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Asphalt shingle roof close-up"
                  className="w-full border-4 border-primary shadow-[8px_8px_0px_0px_#000]"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Problems Section */}
        <section className="py-16 bg-red-50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="font-barlow text-3xl md:text-4xl font-bold uppercase mb-4">
                <span className="text-red-600">Weather Vulnerabilities</span> in Florida
              </h2>
              <p className="font-manrope text-lg text-gray-700 max-w-3xl mx-auto">
                Florida's climate is particularly harsh on asphalt shingles. Here's what you need to know:
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white border-2 border-red-300 shadow-[4px_4px_0px_0px_#DC2626] p-6">
                <div className="bg-red-100 w-16 h-16 rounded-sm flex items-center justify-center mb-4">
                  <Sun className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="font-barlow text-xl font-bold uppercase mb-3">UV Degradation</h3>
                <p className="font-manrope text-gray-700 mb-4">
                  Florida receives some of the highest UV radiation in the country. This causes:
                </p>
                <ul className="space-y-2 font-manrope text-gray-600">
                  <li>• Granule loss (the protective coating)</li>
                  <li>• Cracking and curling of shingles</li>
                  <li>• Color fading and brittleness</li>
                  <li>• Accelerated aging (up to 40% faster)</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-red-300 shadow-[4px_4px_0px_0px_#DC2626] p-6">
                <div className="bg-red-100 w-16 h-16 rounded-sm flex items-center justify-center mb-4">
                  <Wind className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="font-barlow text-xl font-bold uppercase mb-3">Hurricane Damage</h3>
                <p className="font-manrope text-gray-700 mb-4">
                  Asphalt shingles are rated for 60-130 mph winds, but Florida hurricanes can exceed this:
                </p>
                <ul className="space-y-2 font-manrope text-gray-600">
                  <li>• Shingle blow-off during storms</li>
                  <li>• Edge lifting and seal failure</li>
                  <li>• Debris impact damage</li>
                  <li>• Water intrusion from lifted shingles</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-red-300 shadow-[4px_4px_0px_0px_#DC2626] p-6">
                <div className="bg-red-100 w-16 h-16 rounded-sm flex items-center justify-center mb-4">
                  <Droplets className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="font-barlow text-xl font-bold uppercase mb-3">Moisture & Heat</h3>
                <p className="font-manrope text-gray-700 mb-4">
                  High humidity combined with extreme heat creates a perfect storm:
                </p>
                <ul className="space-y-2 font-manrope text-gray-600">
                  <li>• Algae and mold growth (black streaks)</li>
                  <li>• Thermal shock from rapid temp changes</li>
                  <li>• Trapped moisture causing rot</li>
                  <li>• Premature adhesive failure</li>
                </ul>
              </div>
            </div>

            <div className="mt-12 bg-red-600 text-white p-8 border-2 border-black shadow-[6px_6px_0px_0px_#000]">
              <div className="flex items-center gap-4 mb-4">
                <AlertTriangle className="h-10 w-10" />
                <h3 className="font-barlow text-2xl font-bold uppercase">Damage Probability in Florida</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-5xl font-barlow font-bold">85%</p>
                  <p className="font-manrope">of Florida roofs show UV damage within 5 years</p>
                </div>
                <div>
                  <p className="text-5xl font-barlow font-bold">60%</p>
                  <p className="font-manrope">experience storm damage in their lifetime</p>
                </div>
                <div>
                  <p className="text-5xl font-barlow font-bold">40%</p>
                  <p className="font-manrope">shorter lifespan vs. northern states</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="font-barlow text-3xl md:text-4xl font-bold uppercase mb-4">
                <DollarSign className="inline h-10 w-10 text-primary" /> Repair & Replacement Costs
              </h2>
              <p className="font-manrope text-lg text-gray-700 max-w-3xl mx-auto">
                Understanding the true cost of asphalt shingle roof maintenance in Central Florida
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-gray-50 border-2 border-black shadow-[4px_4px_0px_0px_#000] p-8">
                <h3 className="font-barlow text-2xl font-bold uppercase mb-6 text-red-600">
                  Repair Costs
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Minor leak repair</span>
                    <span className="font-barlow font-bold text-xl">$150 - $400</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Shingle replacement (per square)</span>
                    <span className="font-barlow font-bold text-xl">$100 - $350</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Flashing repair</span>
                    <span className="font-barlow font-bold text-xl">$200 - $500</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Storm damage repair</span>
                    <span className="font-barlow font-bold text-xl">$500 - $3,000</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Major structural repair</span>
                    <span className="font-barlow font-bold text-xl">$1,500 - $7,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-manrope font-bold">Professional cleaning (algae/mold)</span>
                    <span className="font-barlow font-bold text-xl">$250 - $600</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border-2 border-black shadow-[4px_4px_0px_0px_#000] p-8">
                <h3 className="font-barlow text-2xl font-bold uppercase mb-6 text-red-600">
                  Full Replacement Costs
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Small home (1,000 sq ft roof)</span>
                    <span className="font-barlow font-bold text-xl">$5,000 - $8,000</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Average home (1,500-2,000 sq ft)</span>
                    <span className="font-barlow font-bold text-xl">$8,000 - $15,000</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Large home (2,500+ sq ft)</span>
                    <span className="font-barlow font-bold text-xl">$15,000 - $30,000</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Tear-off & disposal</span>
                    <span className="font-barlow font-bold text-xl">$1,000 - $2,500</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-manrope">Permit & inspection fees</span>
                    <span className="font-barlow font-bold text-xl">$250 - $500</span>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-red-100 border-2 border-red-300">
                  <p className="font-manrope text-red-800">
                    <strong>Florida Average:</strong> $12,000 - $22,000 for full asphalt shingle replacement
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-16 bg-accent-light">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="font-barlow text-3xl md:text-4xl font-bold uppercase mb-4">
                The <span className="text-primary">Coating Solution</span>
              </h2>
              <p className="font-manrope text-lg text-gray-700 max-w-3xl mx-auto">
                Instead of expensive replacement, professional roof coating can extend your asphalt shingle roof's 
                life by 10-15 years at a fraction of the cost.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-primary w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">UV Protection</h3>
                    <p className="font-manrope text-gray-700">Our coating reflects 85% of solar radiation, preventing granule loss and thermal damage.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-primary w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Droplets className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">Waterproof Seal</h3>
                    <p className="font-manrope text-gray-700">Creates an impermeable barrier that seals cracks and prevents water intrusion.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-primary w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">Same-Day Application</h3>
                    <p className="font-manrope text-gray-700">Most residential roofs are completed in just one day with minimal disruption.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-primary w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">70% Cost Savings</h3>
                    <p className="font-manrope text-gray-700">Coating costs $2-5 per sq ft vs. $8-15 for replacement. Save thousands!</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-black shadow-[6px_6px_0px_0px_#FF8C00] p-8">
                <h3 className="font-barlow text-2xl font-bold uppercase mb-6 text-center">
                  Coating vs. Replacement
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="font-manrope">Average coating cost (1,500 sq ft)</span>
                    <span className="font-barlow font-bold text-xl text-green-600">$3,000 - $7,500</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-manrope">Average replacement cost</span>
                    <span className="font-barlow font-bold text-xl text-red-600">$12,000 - $22,000</span>
                  </div>
                  <div className="border-t-2 border-primary pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-manrope font-bold text-lg">Your Savings</span>
                      <span className="font-barlow font-bold text-2xl text-primary">$9,000 - $14,500</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-secondary text-white">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase mb-6">
              Protect Your Asphalt Shingle Roof Today
            </h2>
            <p className="text-xl font-manrope mb-8">
              Get a FREE inspection and quote. No obligation, no pressure—just honest advice from Central Florida's 
              roof coating experts.
            </p>
            <a href="tel:3218044413">
              <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-white shadow-[4px_4px_0px_0px_#fff] rounded-sm text-xl px-10 py-7 font-barlow uppercase font-bold">
                <Phone className="mr-2 h-6 w-6" />
                Call Now: (321) 804-4413
              </Button>
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black text-white py-8">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <p className="font-manrope text-gray-400">
              © 2026 Savage Rejuvenation Coatings. All Rights Reserved. | Serving Orlando, Tampa & Central Florida
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AsphaltShingles;
